/* eslint-disable */
import { useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Trash2, Plus, AlarmClock, Pencil, PackagePlus } from 'lucide-react';
import { SelectPicker } from 'rsuite';

export const ROW_STATUS_STYLES = {
	matched: { row: 'bg-white', chip: 'bg-white border border-slate-300', text: 'text-slate-800', label: 'Exact match' },
	partial: { row: 'bg-amber-50', chip: 'bg-amber-200', text: 'text-amber-700', label: '≥75% match (review)' },
	ambiguous: {
		row: 'bg-blue-50',
		text: 'text-blue-700',
		label: 'Multiple matches — pick one',
		icon: '?',
	},
	unmatched: { row: 'bg-red-50', chip: 'bg-red-300', text: 'text-red-600', label: 'No product match' },
	userEdited: { row: 'bg-sky-50', chip: 'bg-sky-300', text: 'text-sky-700', label: 'Manually changed' },
};

const RecipeAccordion = (props) => {
	const {
		recipe,
		productList = [],
		recipeProductList = [],
		onToggle,
		onDelete,
		onFieldChange,
		onAddRow,
		onDeleteRow,
		onRowChange,
		onChangeRowProduct,
		onChangeRecipeProduct,
		// Opens the Quick-Create Product modal pre-seeded with the
		// row's pasted name. Sends `{ isRow: true, ... }` upstream so
		// the parent's auto-link path attaches the new product to the
		// ingredient row.
		onCreateProductForRow,
		// Same wrapper for the recipe SECTION header. Sends
		// `{ isRow: false, ... }` so the parent attaches the new
		// product to the recipe (changeRecipeProduct) instead.
		onCreateProductForRecipe,
	} = props;

	const [editingRecipeName, setEditingRecipeName] = useState(false);
	const [pickingForRowId, setPickingForRowId] = useState(null);
	const rowRefs = useRef({});
	const calculatedKg = useMemo(() => {
		return recipe.rows
			.filter((r) => r.use)
			.reduce((sum, r) => sum + parseFloat(r.baseQty || 0), 0);
	}, [recipe.rows]);


	const balanceStatus = useMemo(() => {
		const target = Number(recipe.batchSize) || 0;
		if (target === 0) return 'over';
		if (Math.abs(calculatedKg - target) <= 0.1) return 'balanced';
		if (calculatedKg < target) return 'under';
		return 'over';
	}, [calculatedKg, recipe.batchSize]);

	const balanceStyle = {
		balanced: { cls: 'bg-green-100 text-green-700', label: '✔ Balanced' },
		under: { cls: 'bg-amber-100 text-amber-800', label: '⚠ Under target' },
		over: { cls: 'bg-red-100 text-red-700', label: '✖ Over target' },
	}[balanceStatus];

	const productOptions = useMemo(
		() => (productList || []).flatMap((p) => (
			(p?.variants || []).map((v) => {
				const variantPart = v?.displayName && v.displayName !== '-'
					? ` (${v.displayName})`
					: '';
				const clientIdPart = v?.clientVariantId
					? ` — ${v.clientVariantId}`
					: '';
				return {
					label: `${p?.productName ?? '-'}${variantPart}${clientIdPart}`,
					value: `${p?.id}::${v?.id}`,
				};
			})
		)),
		[productList]
	);

	const recipeProductOptions = useMemo(
		() => (recipeProductList || []).map((p) => ({
			label: p?.productName ?? '-',
			value: p?.id,
		})),
		[recipeProductList]
	);

	const sectionStatus = recipe?.matchStatus || 'matched';
	const sectionDotCls = ROW_STATUS_STYLES[sectionStatus]?.chip || ROW_STATUS_STYLES.matched.chip;
	const sectionTextCls = ROW_STATUS_STYLES[sectionStatus]?.text || ROW_STATUS_STYLES.matched.text;
	const alreadyExists = !!recipe?.alreadyExists;
	const existingRingCls = alreadyExists ? 'ring-1 ring-violet-300' : '';

	return (
		<div className={`border border-slate-300 rounded-lg mb-3 overflow-hidden bg-white shadow-sm ${existingRingCls}`}>
			{/* Header */}
			<div
				className='bg-slate-50 hover:bg-slate-100 px-5 py-3 flex justify-between items-center cursor-pointer border-b border-slate-200'
				onClick={onToggle}
			>
				<div className='flex items-center gap-3 text-slate-800 flex-wrap'>
					{recipe.expanded
						? <ChevronDown className='size-4 text-sky-600' />
						: <ChevronRight className='size-4 text-slate-500' />
					}
					<span
						className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${sectionDotCls}`}
						title={ROW_STATUS_STYLES[sectionStatus]?.label}
					/>

					{editingRecipeName ? (
						<div
							onClick={(e) => e.stopPropagation()}
							className='inline-block'
						>
							<SelectPicker
								size='sm'
								cleanable={false}
								defaultOpen
								autoFocus
								data={recipeProductOptions}
								value={recipe?.productId ?? null}
								placeholder={recipe?.name || 'Select product'}
								placement='bottomStart'
								menuStyle={{ zIndex: 9999, width: 300 }}
								style={{ width: 300 }}
								searchable
								renderValue={(value, item) => (
									<span className='text-slate-800 font-medium'>
										{item?.label || recipe?.name || '-'}
									</span>
								)}
								onChange={(value) => {
									const product = (recipeProductList || []).find((p) => p?.id === value);
									if (product) onChangeRecipeProduct?.(product);
									setEditingRecipeName(false);
								}}
								onClose={() => setEditingRecipeName(false)}
							/>
						</div>
					) : (
						<>
							<span
								onClick={(e) => {
									e.stopPropagation();
									setEditingRecipeName(true);
								}}
								className={`font-semibold text-[15px] cursor-pointer hover:bg-sky-100/60 px-1.5 py-0.5 rounded flex items-center gap-1.5 ${sectionTextCls}`}
								title={`Click to change product${sectionStatus === 'unmatched' ? ' — this product is not matched in our system' : ''}`}
							>
								{recipe.name}
								<Pencil className='size-3 text-slate-400 shrink-0' />
							</span>
							{sectionStatus === 'unmatched' && (
								<button
									type='button'
									onClick={(e) => {
										e.stopPropagation();
										onCreateProductForRecipe?.({
											recipeId: recipe?.id,
											name: recipe?.name || '',
										});
									}}
									className='inline-flex items-center justify-center size-6 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white shrink-0'
									title={`Create new product "${recipe?.name || ''}"`}
								>
									<Plus className='size-3.5' />
								</button>
							)}
						</>
					)}
					{alreadyExists && (
						<span
							onClick={(e) => e.stopPropagation()}
							className='inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-100 text-violet-700 border border-violet-300'
							title={`This product already has a recipe in the system${recipe?.existingRecipeStatus ? ` (${recipe.existingRecipeStatus})` : ''}. Saving will create a new version.`}
						>
							<span className='inline-block w-1.5 h-1.5 rounded-full bg-violet-500' />
							Already created
							{recipe?.existingVersion != null && (
								<span className='font-bold'>· v{recipe.existingVersion}</span>
							)}
						</span>
					)}
					{recipe?._usedIn?.length > 0 && (
						<div
							className='flex flex-wrap items-center gap-1'
							onClick={(e) => e.stopPropagation()}
						>
							<span className='text-[10px] text-slate-500 uppercase tracking-wider'>
								Used in:
							</span>
							{recipe._usedIn.map((parentName, idx) => (
								<span
									key={`${parentName}-${idx}`}
									className='text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded font-medium'
								>
									{parentName}
								</span>
							))}
						</div>
					)}
				</div>
				<button
					type='button'
					onClick={(e) => {
						e.stopPropagation();
						onDelete();
					}}
					className='flex items-center gap-1 px-3 py-1 text-[12px] font-semibold rounded bg-red-500 hover:bg-red-600 text-white'
				>
					<Trash2 className='size-3' />
					Delete Recipe
				</button>
			</div>

			{/* Body */}
			{recipe.expanded && (
				<div className='p-5'>
					<style>{`
						@keyframes alarmRing {
							0%, 85%, 100% { transform: rotate(0deg); }
							88% { transform: rotate(-12deg); }
							91% { transform: rotate(12deg); }
							94% { transform: rotate(-8deg); }
							97% { transform: rotate(6deg); }
						}
						.alarm-ring {
							animation: alarmRing 5s ease-in-out infinite;
							transform-origin: 50% 20%;
							display: inline-block;
						}
					`}</style>

					<div className='grid grid-cols-2 gap-4 mb-5 bg-slate-50 p-4 rounded-lg border border-slate-200 w-1/2'>
						<div className='flex flex-col text-[11px]'>
							<label className='font-bold text-red-500 mb-1.5 uppercase tracking-wider flex items-center gap-1.5'>
								<AlarmClock className='size-3.5 text-red-500 alarm-ring' />
								Total Process Time *
							</label>
							<div className='relative'>
								<input
									type='number'
									step='0.1'
									min='0'
									value={recipe.totalProcessTime}
									onChange={(e) => onFieldChange('totalProcessTime', e.target.value)}
									className='w-full pl-3 pr-12 py-2 border border-slate-300 rounded-md text-[13px] text-slate-700 bg-white focus:outline-none focus:ring-1 focus:ring-sky-500'
								/>
								<span className='absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-slate-400 pointer-events-none'>
									mins
								</span>
							</div>
						</div>
						<div className='flex flex-col text-[11px]'>
							<label className='font-bold text-red-500 mb-1.5 uppercase tracking-wider'>
								Batch Size ({recipe?.unit}) *
							</label>
							<input
								type='number'
								step='0.001'
								value={recipe.batchSize}
								onChange={(e) => onFieldChange('batchSize', e.target.value)}
								className='px-3 py-2 border border-slate-300 rounded-md text-[13px] text-slate-700 bg-white focus:outline-none focus:ring-1 focus:ring-sky-500'
							/>
							{recipe?._batchUnitMismatch && (
								<div className='mt-1.5 text-[11px] leading-snug text-amber-800 bg-amber-50 border border-amber-300 rounded px-2 py-1.5'>
									Pasted unit <span className='font-semibold'>"{recipe._batchUnitMismatch.pastedUnit}"</span> doesn't match the product's unit <span className='font-semibold'>"{recipe._batchUnitMismatch.productUnit}"</span>. Using the product's unit — please verify the batch size value.
								</div>
							)}
						</div>
					</div>


					{recipe?.rows?.length > 0 && (
						<table className='w-full border-collapse text-[13px] mb-3'>
							<thead>
								<tr>
									<th className='w-[50px] text-center bg-blue-100 border border-blue-300 px-2 py-2.5 text-blue-900 font-semibold'>S.No</th>
									<th className='text-left bg-blue-100 border border-blue-300 px-2 py-2.5 text-blue-900 font-semibold'>A: Raw Material (RM)</th>
									<th className='w-[150px] text-left bg-blue-100 border border-blue-300 px-2 py-2.5 text-blue-900 font-semibold'>B: Base Qty</th>
									<th className='w-[110px] text-left bg-blue-100 border border-blue-300 px-2 py-2.5 text-blue-900 font-semibold'>C: Unit</th>
									<th className='w-[160px] text-left bg-blue-100 border border-blue-300 px-2 py-2.5 text-blue-900 font-semibold'>D: Container Weight</th>
									<th className='w-[90px] text-center bg-blue-100 border border-blue-300 px-2 py-2.5 text-blue-900 font-semibold'>E: Use</th>
									<th className='w-[50px] text-center bg-blue-100 border border-blue-300 px-2 py-2.5 text-blue-900 font-semibold'>Actions</th>
								</tr>
							</thead>
							<tbody>
								{recipe.rows.map((row, idx) => {
									const status = row?.matchStatus || 'matched';
									const rowCls = ROW_STATUS_STYLES[status]?.row
										|| (idx % 2 === 1 ? 'bg-slate-50' : 'bg-white');
									const isAmbiguous = status === 'ambiguous';
									const candidateKeys = isAmbiguous && row?.matchCandidates?.length
										? new Set(
											row.matchCandidates.map((c) => `${c.productId}::${c.variantId}`)
										)
										: null;
									const rowPickerOptions = candidateKeys
										? productOptions.filter((opt) => candidateKeys.has(opt.value))
										: productOptions;
									return (
										<tr
											key={row.id}
											className={rowCls}
										>
											<td className='text-center border border-slate-300 p-1'>{idx + 1}</td>
											<td
												className='border border-slate-300 px-2 py-1.5 relative'
												ref={(el) => {
													if (el) {
														rowRefs.current[row.id] = el;
													} else {
														delete rowRefs.current[row.id];
													}
												}}
											>
												{pickingForRowId === row.id ? (
													<SelectPicker
														size='sm'
														block
														cleanable={false}
														defaultOpen
														autoFocus
														data={rowPickerOptions}
														value={row?.productId != null && row?.variantId != null
															? `${row.productId}::${row.variantId}`
															: null}
														placeholder={isAmbiguous
															? `Pick one of ${rowPickerOptions.length} options`
															: (row?.name || 'Select Ingredients')}
														placement='right'
														menuStyle={{ zIndex: 9999, width: 300 }}
														style={{ width: '100%', minWidth: 240 }}
														searchable
														renderValue={(value, item) => (
															<span className='text-slate-800 font-medium'>
																{item?.label || row?.name || '-'}
															</span>
														)}
														onChange={(value) => {
															if (!value) {
																setPickingForRowId(null);
																return;
															}
															const [pidStr, vidStr] = String(value).split('::');
															const product = (productList || []).find(
																(p) => String(p?.id) === pidStr
															);
															const variant = product?.variants?.find(
																(v) => String(v?.id) === vidStr
															) || null;
															if (product) onChangeRowProduct?.(row.id, product, variant);
															setPickingForRowId(null);
														}}
														onClose={() => setPickingForRowId(null)}
														container={() => rowRefs?.current[row.id]}
													/>
												) : (
													<div className='flex items-center gap-1.5'>
														<div
															onClick={() => setPickingForRowId(row.id)}
															className='flex-1 min-w-0 text-slate-800 text-[13px] font-medium cursor-pointer hover:bg-sky-100/60 px-1.5 py-1 rounded flex items-center gap-1.5'
															title={isAmbiguous
																? `Multiple products share this name — click to pick from ${row?.matchCandidates?.length || 0} matches`
																: 'Click to change product'}
														>
															{isAmbiguous && (
																<span
																	className='inline-flex items-center justify-center size-4 rounded-full bg-blue-500 text-white text-[10px] font-bold shrink-0'
																	title='Multiple matches — pick one'
																>
																	?
																</span>
															)}
															<span className='truncate'>{row.name || '-'}</span>
															<Pencil className='size-3 text-slate-400 shrink-0' />
														</div>
														{status === 'unmatched' && (
															<button
																type='button'
																onClick={(e) => {
																	e.stopPropagation();
																	onCreateProductForRow?.({
																		recipeId: recipe?.id,
																		rowId: row?.id,
																		name: row?.name || '',
																	});
																}}
																className='inline-flex items-center justify-center size-6 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white shrink-0'
																title={`Create new product "${row?.name || ''}"`}
															>
																<Plus className='size-3.5' />
															</button>
														)}
													</div>
												)}
											</td>
											<td className='border border-slate-300 p-1'>
												<input
													type='number'
													step='0.001'
													value={row.baseQty}
													onChange={(e) => onRowChange(row.id, 'baseQty', e.target.value)}
													className='w-full px-2 py-1.5 bg-transparent text-slate-700 text-[13px] focus:bg-green-50 focus:outline-none'
												/>
											</td>
											<td className='border border-slate-300 px-2 py-1.5'>
												<div className='text-slate-700 text-[13px]'>
													{row.unit || '-'}
												</div>
											</td>
											<td className='border border-slate-300 p-1'>
												<input
													type='number'
													value={row.containerWeight}
													onChange={(e) => onRowChange(row.id, 'containerWeight', e.target.value)}
													className='w-full px-2 py-1.5 bg-transparent text-slate-700 text-[13px] focus:bg-green-50 focus:outline-none'
												/>
											</td>
											<td className='text-center border border-slate-300 p-1'>
												<input
													type='checkbox'
													checked={row.use}
													onChange={(e) => onRowChange(row.id, 'use', e.target.checked)}
												/>
											</td>
											<td className='text-center border border-slate-300 p-1'>
												<button
													type='button'
													onClick={() => onDeleteRow(row.id)}
													className='text-red-500 hover:bg-red-100 p-1.5 rounded'
												>
													<Trash2 className='size-3.5' />
												</button>
											</td>
										</tr>
									);
								})}
								{/* Σ summary row */}
								<tr className='bg-yellow-200 font-bold text-yellow-900 border-t-2 border-yellow-500'>
									<td className='text-center px-2 py-3 border border-slate-300'>Σ</td>
									<td className='px-2 py-3 border border-slate-300'>Total Calculated Production Batch Size</td>
									<td colSpan={2} className='px-2 py-3 border border-slate-300'>
										{calculatedKg.toFixed(3)} kg
									</td>
									<td colSpan={3} className='px-2 py-3 border border-slate-300'>
										<span
											className={`inline-block px-2 py-1 rounded text-[12px] font-semibold ${balanceStyle.cls}`}
										>
											{balanceStyle.label}
										</span>
									</td>
								</tr>
							</tbody>
						</table>
					)}

					<button
						type='button'
						onClick={onAddRow}
						className='flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-md bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 transition-colors'
					>
						<Plus className='size-3.5' />
						Add Raw Material Row
					</button>
				</div>
			)}
		</div>
	);
};

export default RecipeAccordion;
